import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SearchIcon, BarChart3Icon, PieChartIcon, TableIcon, PlayIcon } from 'lucide-react';
import { premadeReports, reportCategories, ReportDef, ReportFilter } from '../data/reports';
import { RunReportDialog } from '../components/reports/RunReportDialog';
import { useApp } from '../context/AppContext';
const chartIcon = {
  bar: BarChart3Icon,
  line: BarChart3Icon,
  pie: PieChartIcon,
  table: TableIcon
};
function ReportCard({
  report,
  index,
  onRun
}: {
  report: ReportDef;
  index: number;
  onRun: (report: ReportDef) => void;
}) {
  const Icon = chartIcon[report.config.chartType];
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.04,
    duration: 0.3,
    ease: 'easeOut'
  }} data-id="element-376">
      <button type="button" onClick={() => onRun(report)} className="card-float group flex h-full w-full flex-col rounded-2xl bg-white p-5 text-left transition-shadow hover:card-float-lg" data-id="element-377">
        <div className="flex items-start justify-between" data-id="element-378">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy-50 text-navy-700 transition-colors group-hover:bg-navy-900 group-hover:text-gold-300" data-id="element-379">
            <Icon className="h-4 w-4" data-id="element-380" />
          </span>
          <span className="rounded-full bg-navy-50 px-2.5 py-0.5 text-[11px] font-medium text-navy-500" data-id="element-381">
            {report.category}
          </span>
        </div>
        <h3 className="mt-3 text-sm font-semibold text-navy-900" data-id="element-382">
          {report.name}
        </h3>
        <p className="mt-1 flex-1 text-xs leading-relaxed text-navy-400" data-id="element-383">
          {report.description}
        </p>
        <div className="mt-4 flex items-center justify-between" data-id="element-384">
          <span className="text-[11px] text-navy-300" data-id="element-385">
            Last run {report.lastRun}
          </span>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-accent-700 transition-all group-hover:gap-2" data-id="element-386">
            <PlayIcon className="h-3 w-3" data-id="element-387" /> Run
          </span>
        </div>
      </button>
    </motion.div>;
}
export function ReportLibrary() {
  const navigate = useNavigate();
  const {
    presetsForReport,
    savePreset,
    deletePreset
  } = useApp();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('All');
  const [runReportDef, setRunReportDef] = useState<ReportDef | null>(null);
  const filtered = useMemo(() => {
    return premadeReports.filter(r => {
      const matchesCategory = category === 'All' || r.category === category;
      const q = search.toLowerCase();
      const matchesSearch = !q || r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [search, category]);
  const handleRun = (filters: ReportFilter[], presetName: string | null) => {
    if (!runReportDef) return;
    const id = runReportDef.id;
    setRunReportDef(null);
    navigate(`/reports/${id}`, {
      state: {
        filters,
        presetName
      }
    });
  };
  return <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8" data-id="element-388">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-389">
        <div data-id="element-390">
          <h2 className="text-xl font-semibold tracking-tight text-navy-900" data-id="element-391">
            Report Library
          </h2>
          <p className="mt-1 text-sm text-navy-500" data-id="element-392">
            Pre-built reports maintained by SureHire. Run with a saved filter
            preset or set filters on the fly.
          </p>
        </div>
        <div className="relative" data-id="element-393">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-400" data-id="element-394" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter reports…" aria-label="Filter reports" className="card-float h-9 w-full rounded-full bg-white pl-10 pr-4 text-sm text-navy-900 placeholder:text-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-900/10 sm:w-64" data-id="element-395" />
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="Report categories" data-id="element-396">
        {['All', ...reportCategories].map(cat => <button key={cat} type="button" role="tab" aria-selected={category === cat} onClick={() => setCategory(cat)} className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-all ${category === cat ? 'bg-navy-900 text-white' : 'card-float bg-white text-navy-500 hover:text-navy-900'}`} data-id="element-397">
            {cat}
          </button>)}
      </div>

      {filtered.length === 0 ? <div className="card-float rounded-2xl bg-white py-16 text-center" data-id="element-398">
          <p className="text-sm font-medium text-navy-900" data-id="element-399">No reports found</p>
          <p className="mt-1 text-xs text-navy-400" data-id="element-400">
            Try a different search term or category.
          </p>
        </div> : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-id="element-401">
          {filtered.map((report, i) => <ReportCard key={report.id} report={report} index={i} onRun={setRunReportDef} data-id="element-402" />)}
        </div>}

      <RunReportDialog open={runReportDef !== null} report={runReportDef} presets={runReportDef ? presetsForReport(runReportDef.id) : []} onClose={() => setRunReportDef(null)} onRun={handleRun} savePreset={savePreset} deletePreset={deletePreset} data-id="element-403" />
    </div>;
}